import { Drawer, LinearProgress } from '@material-ui/core';
import { cloneDeep } from 'lodash';
import * as React from 'react';
import { CSSTransition } from 'react-transition-group';
import { Button } from '../../../../components/button/Button';
import { FlyoutFooter } from '../../../../components/flyout-components/FlyoutFooter';
import { StatusCode } from '../../../../utils/api';
import { ShipmentWithNotifications } from '../../../../transportation/models/communications/shipment-delivery-schedule';
import { NotificationSendStatus, NotificationSendStatusMetadata } from '../../../../transportation/models/enums/notification-send-status';
import { ShipmentNotificationType, ShipmentNotificationTypeMetadata } from '../../../../transportation/models/enums/notification-type';
import { ShipmentStatus, ShipmentStatusMetadata } from '../../../../transportation/models/enums/shipment-status';
import { ShipmentType } from '../../../../transportation/models/enums/shipment-type';
import { TransportationSvc } from '../../../../transportation/service';

interface IProps {
    isOpen: boolean;
    onClose: any;
    notificationType: ShipmentNotificationType;
    shipments: any[];
}

export const SendNotificationsFlyout = (props: IProps) => {

    const [service] = React.useState(new TransportationSvc());
    const [shipmentsCopy, setShipmentsCopy] = React.useState<ShipmentWithNotifications[]>([]);
    const [wasSuccess, setWasSuccess] = React.useState(false);
    const [canSendAny, setCanSendAny] = React.useState(false);

    enum CanSendReason {
        CanSend = 0,
        InvalidStatus = 1,
        NoDriver = 2,
        InvalidNotificationType = 3,
    };

    /**
     * When isOpen toggles, refresh data
     */
    React.useEffect(() => {
        if (props.isOpen) {
            const t = cloneDeep(props.shipments);
            setShipmentsCopy(t);
            setWasSuccess(false);

            setCanSendAny(false);

            for (let i = 0; i < props.shipments.length; i++) {
                if (getCanSendReason(props.shipments[i]) === CanSendReason.CanSend) {
                    setCanSendAny(true);
                    break;
                }
            }
        }
    }, [props.isOpen]);

    /**
     * Fire our request to send notifications for these shipments
     */
    const sendNotifications = async () => {

        let successes = 0;
        let attempts = 0
        for (let i = 0; i < shipmentsCopy.length; i++) {

            if (getCanSendReason(shipmentsCopy[i]) !== CanSendReason.CanSend) {
                continue;
            }

            attempts++;

            if (shipmentsCopy[i].sendSuccess === NotificationSendStatus.Success) {
                successes++;
                continue;
            }

            shipmentsCopy[i].isLoading = true;
            setShipmentsCopy([...shipmentsCopy]);

            const result = await service.sendNotification(shipmentsCopy[i].id, props.notificationType);
            shipmentsCopy[i].isLoading = false;

            if (result.status === StatusCode.Success) {
                shipmentsCopy[i].createSuccess = NotificationSendStatus.Success;
                successes++;
            }
            else {
                shipmentsCopy[i].createSuccess = NotificationSendStatus.Failure;
            }

            setShipmentsCopy([...shipmentsCopy]);
        }

        setWasSuccess(successes === attempts);
    };

    const closeFlyout = () => {
        props.onClose(true);
    };

    const dismissFlyout = () => {
        props.onClose(false);
    };

    const getCanSendReason = (item: ShipmentWithNotifications) => {
        switch (props.notificationType.value) {
            case ShipmentNotificationTypeMetadata.productionSummary.value:
                return item.status >= ShipmentStatus.Open ? CanSendReason.CanSend : CanSendReason.InvalidStatus;
            case ShipmentNotificationTypeMetadata.preliminaryDelivery.value:
                return item.status >= ShipmentStatus.Open ? CanSendReason.CanSend : CanSendReason.InvalidStatus;
            case ShipmentNotificationTypeMetadata.finalDelivery.value:
                if (item.status >= ShipmentStatus.ReadyToShip) {
                    return (item.type != ShipmentType.Truck || item.driverId) ? CanSendReason.CanSend : CanSendReason.NoDriver;
                }
                else {
                    return CanSendReason.InvalidStatus;
                }
            default:
                return CanSendReason.InvalidNotificationType;
        }
    }

    return (
        <Drawer
            anchor="right"
            onClose={dismissFlyout}
            open={props.isOpen}>
            <div className="stop-action-drawer stop-action-reorder-route production-module-flyout">
                <div className="row">
                    <div className="col">
                        <h1>Send {props.notificationType?.display} Notifications</h1>
                    </div>
                </div>

                <div className="row">
                    <div className="col">
                        <p>Confirm your shipment selections and press the 'Send' button to send these notifications.</p>
                    </div>
                </div>

                <div className="row">
                    <div className="col">
                        {
                            shipmentsCopy
                                ? <table className="table">
                                    <thead>
                                        <tr>
                                            <th>ID</th>
                                            <th>Status</th>
                                            <th>Name</th>
                                            <th>Activity</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {
                                            shipmentsCopy.map((itm: ShipmentWithNotifications, idx: number) =>
                                                <tr key={`delivery-task-${idx}`} className={`delivery-task-${idx}`}>
                                                    <td>{itm.id}</td>
                                                    <td>
                                                        <span className={"text-uppercase shipment-status-color " + ShipmentStatusMetadata[itm.status].class}>
                                                            {ShipmentStatusMetadata[itm.status].display}
                                                        </span>
                                                    </td>
                                                    <td>{itm.name}</td>
                                                    <td>
                                                        {
                                                            getCanSendReason(itm) == CanSendReason.CanSend
                                                                ? (itm.isLoading
                                                                    ? <LinearProgress
                                                                        classes={{
                                                                            colorPrimary: 'btn-progress-indicator-primary',
                                                                            barColorPrimary: 'btn-progress-indicator-barcolor'
                                                                        }}
                                                                    />
                                                                        : NotificationSendStatusMetadata[itm.createSuccess].display)
                                                                    : getCanSendReason(itm) == CanSendReason.InvalidStatus ? 'INVALID STATUS' : 'NO DRIVER'
                                                        }
                                                    </td>
                                                </tr>
                                            )
                                        }
                                    </tbody>
                                </table>
                                : <div>No Tasks Selected.<br />How did you even get here?</div>
                        }
                    </div>
                </div>

                <CSSTransition
                    in={wasSuccess}
                    timeout={300}
                    classNames="productionOrderMenu"
                    unmountOnExit>
                    <div className="row">
                        <div className="col create-success-message">
                            <p>All notifications have been sent.</p>
                            <br />
                            <i className="la la-check-circle" />
                        </div>
                    </div>
                </CSSTransition>

                <FlyoutFooter>
                    <div className="row">
                        <div className="col">
                            {
                                (wasSuccess || !canSendAny)
                                    ? <Button
                                        text="Close"
                                        classes="action"
                                        testID="close-planning-flyout"
                                        onBtnClick={closeFlyout} />
                                    : <Button
                                        text="Send"
                                        classes="attention"
                                        testID="create-planning-flyout"
                                        onBtnClick={sendNotifications} />
                            }
                        </div>
                    </div>
                </FlyoutFooter>

            </div>
        </Drawer>
    );

};
